import { useState, useMemo, useEffect } from 'react';
import {
  Popper,
  InputBase,
  ClickAwayListener,
  Collapse,
  CircularProgress,
} from '@mui/material';
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Search as SearchIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
} from '@mui/icons-material';
import { v1 as uuidv1 } from 'uuid';
import './CollapsibleGroupMultiSelector.scss';

const defaultProps = {
  label: '',
  placeholder: '',
  options: [],
  initFilterKeyValues: [],
  onChange: () => {},
  className: '',
  popperClassName: '',
  disabled: false,
  loading: false,
  dataTestId: '',
};

/**
 * Multi-selector with collapsible groups, optionally nested under sections.
 *
 * options shape: [{ key, name, group, groupSubtitle?, section? }]
 */
const CollapsibleGroupMultiSelector = (p) => {
  const {
    label,
    placeholder,
    options,
    initFilterKeyValues,
    onChange,
    className,
    popperClassName,
    disabled,
    loading,
    dataTestId,
  } = { ...defaultProps, ...p };

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedKeys, setSelectedKeys] = useState(() => new Set(initFilterKeyValues));
  const [searchText, setSearchText] = useState('');
  const [expandedGroups, setExpandedGroups] = useState(() => new Set());
  const [popperId] = useState(() => `cgms-${uuidv1()}`);

  useEffect(() => {
    setSelectedKeys(new Set(initFilterKeyValues));
  }, [initFilterKeyValues]);

  const sections = useMemo(() => {
    const sectionMap = {};
    const sectionOrder = [];

    options.forEach((option) => {
      const sectionName = option.section || '';
      if (!sectionMap[sectionName]) {
        sectionMap[sectionName] = {
          name: sectionName,
          groupMap: {},
          groupOrder: [],
        };
        sectionOrder.push(sectionName);
      }

      const section = sectionMap[sectionName];
      if (!section.groupMap[option.group]) {
        section.groupMap[option.group] = {
          name: option.group,
          subtitle: option.groupSubtitle || '',
          // When group title matches the section, render items flat under the section
          // (avoids "Vizzle Classic" > "Vizzle Classic").
          flat: Boolean(sectionName && option.group === sectionName),
          items: [],
        };
        section.groupOrder.push(option.group);
      }
      section.groupMap[option.group].items.push(option);
    });

    return sectionOrder.map((sectionName) => ({
      name: sectionName,
      groups: sectionMap[sectionName].groupOrder.map((g) => sectionMap[sectionName].groupMap[g]),
    }));
  }, [options]);

  // Expand all groups by default when options load
  useEffect(() => {
    const groupNames = sections.flatMap((section) => (
      section.groups.filter((g) => !g.flat).map((g) => g.name)
    ));
    setExpandedGroups(new Set(groupNames));
  }, [sections]);

  const open = Boolean(anchorEl);
  const id = open ? popperId : undefined;
  const isDisabled = disabled || loading;

  const handleOpen = (event) => {
    if (isDisabled) return;
    setAnchorEl(event.currentTarget);
    setSearchText('');
  };

  const handleClose = () => {
    if (anchorEl) anchorEl.focus();
    setAnchorEl(null);
  };

  const handleToggleGroup = (groupName) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupName)) {
        next.delete(groupName);
      } else {
        next.add(groupName);
      }
      return next;
    });
  };

  const handleToggleItem = (key) => {
    const next = new Set(selectedKeys);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    setSelectedKeys(next);
    onChange(options.filter((o) => next.has(o.key)));
  };

  const handleClearAll = () => {
    setSelectedKeys(new Set());
    onChange([]);
  };

  const filteredSections = useMemo(() => {
    if (!searchText.trim()) return sections;
    const lower = searchText.toLowerCase();
    return sections
      .map((section) => ({
        ...section,
        groups: section.groups
          .map((g) => ({
            ...g,
            items: g.items.filter((item) => item.name.toLowerCase().includes(lower)),
          }))
          .filter((g) => g.items.length > 0),
      }))
      .filter((section) => section.groups.length > 0);
  }, [sections, searchText]);

  const selectedCount = selectedKeys.size;

  const displayValue = useMemo(() => {
    if (selectedCount === 0) return '';
    return options
      .filter((o) => selectedKeys.has(o.key))
      .map((o) => o.name)
      .join(', ');
  }, [selectedKeys, options, selectedCount]);

  const renderItem = (item) => {
    const isSelected = selectedKeys.has(item.key);
    return (
      <div
        key={item.key}
        className='cgms-item'
        role='option'
        aria-selected={isSelected}
        tabIndex={0}
        onClick={() => handleToggleItem(item.key)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleToggleItem(item.key);
          }
        }}
      >
        {isSelected
          ? <CheckBoxIcon color='primary' fontSize='small' className='cgms-item-checkbox' aria-hidden='true' data-test={`${dataTestId}-item-checkbox-${item.key}`} />
          : <CheckBoxOutlineBlankIcon color='primary' fontSize='small' className='cgms-item-checkbox' aria-hidden='true' data-test={`${dataTestId}-item-checkbox-${item.key}`} />}
        <span className='cgms-item-name'>{item.name}</span>
      </div>
    );
  };

  const renderGroup = (group, sectionName) => {
    const isExpanded = expandedGroups.has(group.name);
    const groupSelectedCount = group.items.filter((item) => selectedKeys.has(item.key)).length;
    const groupKey = sectionName ? `${sectionName}::${group.name}` : group.name;
    const groupItemsId = `cgms-group-items-${groupKey.replace(/\s+/g, '-')}`;

    // eslint-disable-next-line no-nested-ternary
    const listboxLabel = group.subtitle
      ? `${group.subtitle} ${group.name}`
      : (sectionName && sectionName !== group.name ? `${sectionName}: ${group.name}` : group.name);

    if (group.flat) {
      return (
        <div key={groupKey} className='cgms-group cgms-group--flat' data-test={`${dataTestId}-group-${group.name}`}>
          <div
            id={groupItemsId}
            className='cgms-group-items'
            role='listbox'
            aria-multiselectable='true'
            aria-label={listboxLabel || label}
            data-test={`${dataTestId}-group-items-${group.name}`}
          >
            {group.items.map(renderItem)}
          </div>
        </div>
      );
    }

    return (
      <div key={groupKey} className='cgms-group' data-test={`${dataTestId}-group-${group.name}`}>
        <button
          type='button'
          className='cgms-group-header'
          data-test={`${dataTestId}-group-header-${group.name}`}
          onClick={() => handleToggleGroup(group.name)}
          aria-expanded={isExpanded}
          aria-controls={groupItemsId}
        >
          {isExpanded
            ? <ExpandLessIcon className='cgms-group-chevron' aria-hidden='true' />
            : <ExpandMoreIcon className='cgms-group-chevron' aria-hidden='true' />}
          <div className='cgms-group-text'>
            {group.subtitle && (
              <span className='cgms-group-subtitle'>{group.subtitle}</span>
            )}
            <span className='cgms-group-name'>{group.name}</span>
          </div>
          {groupSelectedCount > 0 && (
            <span
              className='cgms-group-badge'
              aria-label={`${groupSelectedCount} selected in this group`}
            >
              {groupSelectedCount}
            </span>
          )}
        </button>

        <Collapse in={isExpanded}>
          <div
            id={groupItemsId}
            className='cgms-group-items'
            role='listbox'
            aria-multiselectable='true'
            aria-label={listboxLabel || label}
            data-test={`${dataTestId}-group-items-${group.name}`}
          >
            {group.items.map(renderItem)}
          </div>
        </Collapse>
      </div>
    );
  };

  return (
    <>
      <div className={`collapsible-group-multi-selector ${className}`} data-test={`${dataTestId}-trigger`}>
        <button
          type='button'
          className={`cgms-trigger ${open ? 'cgms-trigger--open' : ''} ${loading ? 'cgms-trigger--loading' : ''}`}
          aria-describedby={id}
          aria-haspopup='dialog'
          aria-expanded={open}
          aria-busy={loading}
          // eslint-disable-next-line no-nested-ternary
          aria-label={displayValue ? `${label}: ${displayValue}` : (placeholder ? `${label}: ${placeholder}` : label)}
          onClick={handleOpen}
          disabled={isDisabled}
          data-test={`${dataTestId}-trigger-button`}
        >
          <div className='cgms-trigger-content' data-test={`${dataTestId}-trigger-content`}>
            <span className='cgms-trigger-label'>{label}</span>
            {(displayValue || placeholder) && (
              <span
                className={`cgms-trigger-value${!displayValue ? ' cgms-trigger-value--placeholder' : ''}`}
                aria-hidden='true'
              >
                {displayValue || placeholder}
              </span>
            )}
          </div>
          {loading ? (
            <CircularProgress
              className='cgms-trigger-spinner'
              size={20}
              data-test={`${dataTestId}-loading`}
            />
          ) : (
            <KeyboardArrowDownIcon className={`cgms-trigger-arrow ${open ? 'cgms-trigger-arrow--open' : ''}`} />
          )}
        </button>
      </div>

      <ClickAwayListener mouseEvent='onMouseDown' touchEvent='onTouchStart' onClickAway={handleClose}>
        <Popper
          id={id}
          open={open}
          anchorEl={anchorEl}
          placement='bottom-start'
          className={`cgms-popper ${popperClassName}`}
          role='dialog'
          aria-label={label}
          aria-modal='true'
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              handleClose();
            }
          }}
          data-test={`${dataTestId}-popper`}
        >
          {/* Search */}
          <div className='cgms-search' data-test={`${dataTestId}-search`}>
            <SearchIcon className='cgms-search-icon' aria-hidden='true' />
            <InputBase
              className='cgms-search-input'
              placeholder='Search'
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              inputProps={{ 'aria-label': `Search ${label}` }}
              autoFocus
            />
          </div>

          {/* Selection summary */}
          <div className='cgms-summary' aria-live='polite' aria-atomic='true' data-test={`${dataTestId}-summary`}>
            <span className='cgms-selected-count'>
              {selectedCount} selected
            </span>
            {selectedCount > 0 && (
              <button
                type='button'
                className='cgms-clear-btn'
                data-test={`${dataTestId}-clear-btn`}
                onClick={handleClearAll}
              >
                Clear Selections
              </button>
            )}
          </div>

          <div
            className='cgms-groups'
            data-test={`${dataTestId}-groups`}
          >
            {filteredSections.length === 0 && (
              <div className='cgms-no-results'>No results found</div>
            )}
            {filteredSections.map((section) => (
              <div
                key={section.name || 'default'}
                className={`cgms-section${!section.name ? ' cgms-section--untitled' : ''}`}
                data-test={`${dataTestId}-section-${section.name || 'default'}`}
              >
                {section.name && (
                  <div className='cgms-section-header' data-test={`${dataTestId}-section-header-${section.name}`}>
                    {section.name}
                  </div>
                )}
                {section.groups.map((group) => renderGroup(group, section.name))}
              </div>
            ))}
          </div>
        </Popper>
      </ClickAwayListener>
    </>
  );
};

export default CollapsibleGroupMultiSelector;
